// src/components/approvals/ApprovalsAdminPage.tsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios, { AxiosError } from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Autocomplete,
  InputAdornment,
  Chip,
  Avatar,
  Tabs,
  Tab,
  Paper,
  Skeleton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SearchIcon from "@mui/icons-material/Search";
import { useForm, Controller, Control  } from "react-hook-form";
import { baseUrl } from "../../constants";
import { useNavigate } from "react-router-dom";
import { getUserInfoFromToken } from "../../utils/Main";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import WorkIcon from "@mui/icons-material/Work";
import ApartmentIcon from "@mui/icons-material/Apartment";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import TimelineIcon from "@mui/icons-material/Timeline";
import ListAltIcon from "@mui/icons-material/ListAlt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Stepper, Step, StepLabel, StepContent } from "@mui/material";
import React from "react";

/** ========== Types (keep aligned with backend) ========== */
type ApprovalActorType =
  | "USER"
  | "POSITION"
  | "ROLE"
  | "MANAGER_OF_REQUESTER"
  | "DEPARTMENT_MANAGERS"
  | "PROJECT_MANAGERS";

type ApprovalRequiredStrategy = "ALL" | "ANY";

type ApprovalStep = {
  id: number;
  policyId: number;
  stepOrder: number;
  actorType: ApprovalActorType;
  requiredStrategy: ApprovalRequiredStrategy;
  actorUserId?: number | null;
  actorPositionId?: number | null;
  actorRole?: string | null;
  sameDepartmentAsRequester: boolean;
  useTaskProject: boolean;
  useProjectDepartment: boolean;
  targetDepartmentId?: number | null;
  targetProjectId?: number | null;
  autoAdvanceIfNone: boolean;
};

type ApprovalPolicy = {
  id: number;
  workspaceId: number;
  name: string;
  taskType: string;
  version: number;
  isActive: boolean;
  steps: ApprovalStep[];
};

type PolicyForm = {
  workspaceId: number | string;
  name: string;
  taskType: string;
  version?: number | string;
  isActive?: boolean;
};

type StepForm = {
  stepOrder: number | string;
  actorType: ApprovalActorType;
  requiredStrategy?: ApprovalRequiredStrategy;
  actorUserId?: number | string | null;
  actorPositionId?: number | string | null;
  actorRole?: string | null;
  sameDepartmentAsRequester?: boolean;
  useTaskProject?: boolean;
  useProjectDepartment?: boolean;
  targetDepartmentId?: number | string | null;
  targetProjectId?: number | string | null;
  autoAdvanceIfNone?: boolean;
};

type Dept = { id: number; name?: string; title?: string; workspaceId?: number };
type Proj = { id: number; name?: string; title?: string; departmentId?: number };
type Pos = { id: number; name?: string; title?: string; departmentId?: number };
type UserLite = {
  id: number;
  username?: string;
  name?: string;
  email?: string;
  profile?: { firstName?: string; lastName?: string; name?: string; imageUrl?: string };
};

/** ========== Utils ========== */
const tokenHeader = () => {
  const t = localStorage.getItem("accessToken") || localStorage.getItem("token");
  return { Authorization: `Bearer ${t}` };
};
const toInt = (v: any): number | undefined =>
  v === "" || v === null || v === undefined ? undefined : Number.parseInt(String(v), 10);
const toPolicyArray = (payload: any): ApprovalPolicy[] =>
  Array.isArray(payload) ? payload : payload?.items && Array.isArray(payload.items) ? payload.items : [];
const isHtmlResponse = (payload: any) => typeof payload === "string" && /<html/i.test(payload);

const deptLabel = (d?: Dept | null) => (d?.name || d?.title || `Dept #${d?.id ?? ""}`).toString();
const projLabel = (p?: Proj | null) => (p?.name || p?.title || `Project #${p?.id ?? ""}`).toString();
const posLabel = (p?: Pos | null) => (p?.name || p?.title || `Position #${p?.id ?? ""}`).toString();
const userLabel = (u?: UserLite | null) => {
  if (!u) return "";
  // Priority: profile.name > firstName+lastName > name > username > email > id
  const profileName = u.profile?.name;
  const firstName = u.profile?.firstName;
  const lastName = u.profile?.lastName;
  const fullName = firstName || lastName ? `${firstName || ""} ${lastName || ""}`.trim() : "";
  return profileName || fullName || u.name || u.username || u.email || `User #${u.id}`;
};
const avatarInitials = (u?: UserLite | null) => {
  const n = userLabel(u);
  if (!n) return "";
  const parts = n.split(" ").filter(Boolean);
  return (parts[0]?.[0] ?? "").toUpperCase() + (parts[1]?.[0] ?? "").toUpperCase();
};

/** ========== Local caches (performance) ========== */
const usersCache = new Map<number, UserLite>();
const positionsCache = new Map<number, Pos>();
const departmentsCache = new Map<number, Dept>();
const projectsCache = new Map<number, Proj>();

/** ========== Child: Step card (memoized) ========== */
type StepCardProps = {
  step: ApprovalStep;
  idx: number;
  total: number;
  dicts: {
    users: Map<number, UserLite>;
    positions: Map<number, Pos>;
    departments: Map<number, Dept>;
    projects: Map<number, Proj>;
  };
  onMove: (index: number, dir: -1 | 1) => void;
  onEdit: (step: ApprovalStep) => void;
  onDelete: (id: number) => void;
};

const StepCard = React.memo(function StepCard({
  step: s,
  idx,
  total,
  dicts,
  onMove,
  onEdit,
  onDelete,
}: StepCardProps) {
  const header = (
    <Stack direction="row" spacing={1} flexWrap="wrap">
      <Chip size="small" label={`Step ${s.stepOrder}`} icon={<TimelineIcon fontSize="small" />} />
      <Chip
        size="small"
        label={s.actorType}
        color="primary"
        icon={
          s.actorType === "USER" ? (
            <PersonIcon />
          ) : s.actorType === "POSITION" ? (
            <WorkIcon />
          ) : s.actorType === "ROLE" ? (
            <GroupIcon />
          ) : s.actorType === "PROJECT_MANAGERS" ? (
            <RocketLaunchIcon />
          ) : s.actorType === "DEPARTMENT_MANAGERS" ? (
            <ApartmentIcon />
          ) : (
            <InfoOutlinedIcon />
          )
        }
      />
      <Chip
        size="small"
        label={s.requiredStrategy === "ALL" ? "ALL approvers" : "ANY approver"}
        color={s.requiredStrategy === "ALL" ? "secondary" : "success"}
        icon={<CheckCircleIcon />}
      />
      {s.autoAdvanceIfNone && <Chip size="small" label="Auto-advance if none" variant="outlined" />}
    </Stack>
  );

  const u = s.actorUserId ? dicts.users.get(s.actorUserId) : undefined;
  const p = s.actorPositionId ? dicts.positions.get(s.actorPositionId) : undefined;
  const d = s.targetDepartmentId ? dicts.departments.get(s.targetDepartmentId) : undefined;
  const pr = s.targetProjectId ? dicts.projects.get(s.targetProjectId) : undefined;

  const details = (
    <Stack spacing={1} mt={1}>
      {s.actorType === "USER" && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar sx={{ width: 28, height: 28 }}>{avatarInitials(u)}</Avatar>
          <Typography variant="body2">
            <b>Approver:</b> {userLabel(u) || `User #${s.actorUserId}`}
          </Typography>
        </Stack>
      )}
      {s.actorType === "POSITION" && (
        <Typography variant="body2">
          <b>Position:</b> {posLabel(p) || `Position #${s.actorPositionId}`}
        </Typography>
      )}
      {s.actorType === "ROLE" && (
        <Typography variant="body2">
          <b>Role:</b> {s.actorRole || "-"}
        </Typography>
      )}
      {(s.actorType === "ROLE" || s.actorType === "POSITION") && (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip size="small" label={`useTaskProject: ${s.useTaskProject ? "Yes" : "No"}`} />
          <Chip size="small" label={`useProjectDepartment: ${s.useProjectDepartment ? "Yes" : "No"}`} />
          {s.sameDepartmentAsRequester && <Chip size="small" label="sameDepartmentAsRequester" />}
        </Stack>
      )}
      {(d || pr) && (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {d && <Chip size="small" variant="outlined" label={`Dept: ${deptLabel(d)}`} />}
          {pr && <Chip size="small" variant="outlined" label={`Project: ${projLabel(pr)}`} />}
        </Stack>
      )}
    </Stack>
  );

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
        <Box flex={1}>
          {header}
          {details}
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Move up">
            <span>
              <IconButton disabled={idx === 0} onClick={() => onMove(idx, -1)}>
                <ArrowUpwardIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move down">
            <span>
              <IconButton disabled={idx === total - 1} onClick={() => onMove(idx, +1)}>
                <ArrowDownwardIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton onClick={() => onEdit(s)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton color="error" onClick={() => onDelete(s.id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
});

/** ========== Main component ========== */
export default function ApprovalsAdminPage() {
  const navigate = useNavigate();
  const user = getUserInfoFromToken();
  const workspaceId = user?.workspaceId;

  const [policies, setPolicies] = useState<ApprovalPolicy[]>([]);
  const [selectedPolicyId, setSelectedPolicyId] = useState<number | null>(null);
  const [selectedPolicy, setSelectedPolicy] = useState<ApprovalPolicy | null>(null);

  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" }>({
    open: false,
    message: "",
    severity: "info",
  });

  const [viewTab, setViewTab] = useState<"list" | "diagram">("list");

  const notify = useCallback((message: string, severity: "success" | "error" | "info" = "success") => {
    setSnack({ open: true, message, severity });
  }, []);

  const handleErr = useCallback((err: unknown, fallback = "Something went wrong") => {
    const msg =
      (err as AxiosError)?.response?.data && typeof (err as AxiosError).response?.data === "object"
        ? JSON.stringify((err as AxiosError).response?.data)
        : (err as Error)?.message || fallback;
    notify(msg, "error");
    // eslint-disable-next-line no-console
    console.error(err);
  }, [notify]);

  /** -------- AbortControllers to cancel in-flight calls -------- */
  const listCtrlRef = useRef<AbortController | null>(null);
  const detailCtrlRef = useRef<AbortController | null>(null);
  const userCtrlRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const token = localStorage.getItem("accessToken") || localStorage.getItem("token");
    if (!token) navigate("/");
  }, [navigate]);

  /** -------- Policies: list (lightweight) -------- */
  const loadPolicies = useCallback(async () => {
    setBusy(true);
    listCtrlRef.current?.abort();
    const ctrl = new AbortController();
    listCtrlRef.current = ctrl;
    try {
      const res = await axios.get(`${baseUrl}/approval-policies`, {
        headers: tokenHeader(),
        signal: ctrl.signal as any,
      });
      const payload = res.data;
      if (isHtmlResponse(payload)) {
        notify(`Got HTML from ${baseUrl}/approval-policies. Check baseUrl or proxy.`, "error");
        setPolicies([]);
        return;
      }
      setPolicies(toPolicyArray(payload));
    } catch (e) {
      if ((e as any)?.name !== "CanceledError") {
        handleErr(e, "Failed to load policies");
        setPolicies([]);
      }
    } finally {
      setBusy(false);
    }
  }, [handleErr, notify]);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  /** -------- Select single policy (fetch details only when needed) -------- */
  const selectPolicy = useCallback(
    async (id: number) => {
      setSelectedPolicyId(id);
      setSelectedPolicy(null);
      setBusy(true);
      detailCtrlRef.current?.abort();
      const ctrl = new AbortController();
      detailCtrlRef.current = ctrl;
      try {
        const { data } = await axios.get<ApprovalPolicy>(`${baseUrl}/approval-policies/${id}`, {
          headers: tokenHeader(),
          signal: ctrl.signal as any,
        });
        setSelectedPolicy(data);
        // hydrate only visible steps data (lazy resolve)
        await hydrateVisibleDictionaries(data.steps || []);
      } catch (e) {
        if ((e as any)?.name !== "CanceledError") {
          handleErr(e, "Failed to load policy");
          setSelectedPolicy(null);
        }
      } finally {
        setBusy(false);
      }
    },
    [handleErr]
  );

  /** -------- Dictionaries (lazy, cached) -------- */
  const fetchProfileByUserId = useCallback(async (id: number): Promise<UserLite | null> => {
    if (usersCache.has(id)) return usersCache.get(id)!;
    try {
      const { data } = await axios.get(`${baseUrl}/profile/${id}`, { headers: tokenHeader() });
      const userLite: UserLite = {
        id,
        username: data?.name || data?.email,
        email: data?.email,
        profile: {
          firstName: data?.firstName,
          lastName: data?.lastName,
          imageUrl: data?.imageUrl,
        },
      };
      usersCache.set(id, userLite);
      return userLite;
    } catch {
      return null;
    }
  }, []);

  const fetchPositionById = useCallback(async (id: number): Promise<Pos | null> => {
    if (positionsCache.has(id)) return positionsCache.get(id)!;
    try {
      const { data } = await axios.get(`${baseUrl}/positions/${id}`, { headers: tokenHeader() });
      if (data?.id) {
        positionsCache.set(id, data);
        return data;
      }
    } catch {}
    return null;
  }, []);

  const fetchDepartmentById = useCallback(async (id: number): Promise<Dept | null> => {
    if (departmentsCache.has(id)) return departmentsCache.get(id)!;
    try {
      // fallback endpoint names are allowed in your API
      const endpoints = [`${baseUrl}/departments/by-id/${id}`, `${baseUrl}/departments/${id}`];
      for (const url of endpoints) {
        try {
          const { data } = await axios.get(url, { headers: tokenHeader() });
          if (data?.id) {
            departmentsCache.set(id, data);
            return data;
          }
        } catch {
          // try next
        }
      }
    } catch {}
    return null;
  }, []);

  const fetchProjectById = useCallback(async (id: number): Promise<Proj | null> => {
    if (projectsCache.has(id)) return projectsCache.get(id)!;
    try {
      const { data } = await axios.get(`${baseUrl}/projects/${id}`, { headers: tokenHeader() });
      if (data?.id) {
        projectsCache.set(id, data);
        return data;
      }
    } catch {}
    return null;
  }, []);

  const hydrateVisibleDictionaries = useCallback(
    async (steps: ApprovalStep[]) => {
      const promises: Promise<any>[] = [];
      steps.forEach((s) => {
        if (s.actorUserId && !usersCache.has(s.actorUserId)) {
          promises.push(fetchProfileByUserId(s.actorUserId));
        }
        if (s.actorPositionId && !positionsCache.has(s.actorPositionId)) {
          promises.push(fetchPositionById(s.actorPositionId));
        }
        if (s.targetDepartmentId && !departmentsCache.has(s.targetDepartmentId)) {
          promises.push(fetchDepartmentById(s.targetDepartmentId));
        }
        if (s.targetProjectId && !projectsCache.has(s.targetProjectId)) {
          promises.push(fetchProjectById(s.targetProjectId));
        }
      });
      if (promises.length) {
        await Promise.allSettled(promises);
        // force re-render to reflect cache updates
        setTick((t) => t + 1);
      }
    },
    [fetchDepartmentById, fetchPositionById, fetchProfileByUserId, fetchProjectById]
  );

  /** small state bump to reflect cache changes */
  const [tick, setTick] = useState(0);

  /** -------- Create / Edit Policy -------- */
  const {
    control: pc,
    handleSubmit: submitPolicy,
    reset: resetPolicy,
  } = useForm<PolicyForm>({ defaultValues: { workspaceId: 1, name: "", taskType: "", version: 1, isActive: true } });

  const openCreatePolicy = useCallback(() => {
    setSelectedPolicyId(null);
    setSelectedPolicy(null);
    resetPolicy({ workspaceId: workspaceId ?? 1, name: "", taskType: "", version: 1, isActive: true });
    setOpenPolicyDialog(true);
  }, [resetPolicy, workspaceId]);

  const openEditPolicy = useCallback((policy: ApprovalPolicy) => {
    setSelectedPolicyId(policy.id);
    setSelectedPolicy(policy);
    resetPolicy({
      workspaceId: policy.workspaceId,
      name: policy.name,
      taskType: policy.taskType,
      version: policy.version,
      isActive: policy.isActive,
    });
    setOpenPolicyDialog(true);
  }, [resetPolicy]);

  const onCreatePolicy = submitPolicy(async (values) => {
    setBusy(true);
    try {
      const payload = {
        workspaceId: toInt(values.workspaceId) ?? 1,
        name: values.name?.trim() ?? "",
        taskType: values.taskType?.trim() ?? "",
        version: toInt(values.version),
        isActive: !!values.isActive,
      };
      const { data } = await axios.post<ApprovalPolicy>(`${baseUrl}/approval-policies`, payload, { headers: tokenHeader() });
      notify("Policy created");
      setOpenPolicyDialog(false);
      await selectPolicy(data.id);
      await loadPolicies();
    } catch (e) {
      handleErr(e, "Failed to create policy");
    } finally {
      setBusy(false);
    }
  });

  const onUpdatePolicy = submitPolicy(async (values) => {
    if (!selectedPolicy) return;
    setBusy(true);
    try {
      const payload = {
        name: values.name?.trim() ?? "",
        taskType: values.taskType?.trim() ?? "",
        version: toInt(values.version),
        isActive: !!values.isActive,
      };
      await axios.patch(`${baseUrl}/approval-policies/${selectedPolicy.id}`, payload, { headers: tokenHeader() });
      notify("Policy updated");
      await selectPolicy(selectedPolicy.id);
      await loadPolicies();
    } catch (e) {
      handleErr(e, "Failed to update policy");
    } finally {
      setBusy(false);
    }
  });

  /** -------- Catalogs for pickers (loaded on demand) -------- */
  const [deptOptions, setDeptOptions] = useState<Dept[]>([]);
  const [projOptions, setProjOptions] = useState<Proj[]>([]);
  const [posOptions, setPosOptions] = useState<Pos[]>([]);
  const [loadingDepts, setLoadingDepts] = useState(false);
  const [loadingProjs, setLoadingProjs] = useState(false);
  const [loadingPos, setLoadingPos] = useState(false);

  const loadDepts = useCallback(async () => {
    setLoadingDepts(true);
    try {
      // Use same endpoint as Assignment page
      const { data } = await axios.get(`${baseUrl}/departments`, {
        headers: tokenHeader(),
      });
      setDeptOptions(Array.isArray(data) ? data : []);
    } catch (e) {
      handleErr(e, "Failed to load departments");
      setDeptOptions([]);
    } finally {
      setLoadingDepts(false);
    }
  }, [handleErr]);

  const loadProjs = useCallback(async () => {
    setLoadingProjs(true);
    try {
      const { data } = await axios.get(`${baseUrl}/projects`, { headers: tokenHeader() });
      setProjOptions(Array.isArray(data) ? data : []);
    } catch (e) {
      handleErr(e, "Failed to load projects");
      setProjOptions([]);
    } finally {
      setLoadingProjs(false);
    }
  }, [handleErr]);

  const loadPos = useCallback(async () => {
    setLoadingPos(true);
    try {
      const { data } = await axios.get(`${baseUrl}/positions`, { headers: tokenHeader() });
      setPosOptions(Array.isArray(data) ? data : []);
    } catch (e) {
      handleErr(e, "Failed to load positions");
      setPosOptions([]);
    } finally {
      setLoadingPos(false);
    }
  }, [handleErr]);

  /** -------- Step Dialog (add/edit) -------- */
  const defaultStep: StepForm = {
    stepOrder: 1,
    actorType: "DEPARTMENT_MANAGERS",
    requiredStrategy: "ALL",
    sameDepartmentAsRequester: false,
    useTaskProject: true,
    useProjectDepartment: true,
    autoAdvanceIfNone: true,
  };

  const { control: sc, handleSubmit: submitStep, reset: resetStep, watch, setValue } = useForm<StepForm>({ defaultValues: defaultStep });
  const actorType = watch("actorType");
  const [openPolicyDialog, setOpenPolicyDialog] = useState(false);
  const [openStepDialog, setOpenStepDialog] = useState<{ open: boolean; stepId?: number }>({ open: false });

  const openAddStep = useCallback(() => {
    const nextOrder = (selectedPolicy?.steps?.length ?? 0) + 1;
    resetStep({ ...defaultStep, stepOrder: nextOrder });
    setUserScope("DEPARTMENT");
    setSelectedDeptId(null);
    setSelectedProjId(null);
    setSelectedPosId(null);
    setUserQ("");
    setUserOptions([]);
    setOpenStepDialog({ open: true });
  }, [resetStep, selectedPolicy?.steps?.length]);

  const openEditStep = useCallback(
    async (step: ApprovalStep) => {
      resetStep({
        stepOrder: step.stepOrder,
        actorType: step.actorType,
        requiredStrategy: step.requiredStrategy,
        actorUserId: step.actorUserId ?? null,
        actorPositionId: step.actorPositionId ?? null,
        actorRole: step.actorRole ?? "",
        sameDepartmentAsRequester: step.sameDepartmentAsRequester,
        useTaskProject: step.useTaskProject,
        useProjectDepartment: step.useProjectDepartment,
        targetDepartmentId: step.targetDepartmentId ?? null,
        targetProjectId: step.targetProjectId ?? null,
        autoAdvanceIfNone: step.autoAdvanceIfNone,
      });

      // prefill chooser lists for user/position (hydrated from caches or fetched once)
      if (step.actorType === "USER" && step.actorUserId) {
        if (!usersCache.has(step.actorUserId)) await fetchProfileByUserId(step.actorUserId);
        const u = usersCache.get(step.actorUserId);
        if (u) {
          setUserOptions([u]);
          setValue("actorUserId", u.id);
        } else {
          setUserOptions([]);
        }
      } else {
        setUserOptions([]);
      }

      if (step.actorType === "POSITION" && step.actorPositionId) {
        if (!positionsCache.has(step.actorPositionId)) await fetchPositionById(step.actorPositionId);
        const p = positionsCache.get(step.actorPositionId);
        if (p) {
          setPosOptions((prev) => (prev.some((x) => x.id === p.id) ? prev : [p, ...prev]));
          setValue("actorPositionId", p.id);
        }
      }

      setUserScope("ALL");
      setSelectedDeptId(null);
      setSelectedProjId(null);
      setSelectedPosId(null);
      setUserQ("");
      setOpenStepDialog({ open: true, stepId: step.id });
    },
    [fetchPositionById, fetchProfileByUserId, resetStep, setValue]
  );

  const onSubmitStep = submitStep(async (values) => {
    if (!selectedPolicy) return;
    setBusy(true);
    try {
      const payload = {
        stepOrder: toInt(values.stepOrder) ?? 1,
        actorType: values.actorType,
        requiredStrategy: values.requiredStrategy ?? "ALL",
        actorUserId:
          values.actorUserId === "" || values.actorUserId === null || values.actorUserId === undefined
            ? null
            : toInt(values.actorUserId) ?? null,
        actorPositionId:
          values.actorPositionId === "" || values.actorPositionId === null || values.actorPositionId === undefined
            ? null
            : toInt(values.actorPositionId) ?? null,
        actorRole: values.actorRole && values.actorRole.trim() ? values.actorRole.trim() : null,
        sameDepartmentAsRequester: !!values.sameDepartmentAsRequester,
        useTaskProject: !!values.useTaskProject,
        useProjectDepartment: !!values.useProjectDepartment,
        targetDepartmentId:
          values.targetDepartmentId === "" || values.targetDepartmentId === null || values.targetDepartmentId === undefined
            ? null
            : toInt(values.targetDepartmentId) ?? null,
        targetProjectId:
          values.targetProjectId === "" || values.targetProjectId === null || values.targetProjectId === undefined
            ? null
            : toInt(values.targetProjectId) ?? null,
        autoAdvanceIfNone: !!values.autoAdvanceIfNone,
      };

      if (openStepDialog.stepId) {
        await axios.patch(`${baseUrl}/approval-policies/${selectedPolicy.id}/steps/${openStepDialog.stepId}`, payload, { headers: tokenHeader() });
        notify("Step updated");
      } else {
        await axios.post(`${baseUrl}/approval-policies/${selectedPolicy.id}/steps`, payload, { headers: tokenHeader() });
        notify("Step added");
      }
      setOpenStepDialog({ open: false });
      await selectPolicy(selectedPolicy.id);
    } catch (e) {
      handleErr(e, "Failed to save step");
    } finally {
      setBusy(false);
    }
  });

  const deleteStepAction = useCallback(
    async (stepId: number) => {
      if (!selectedPolicy) return;
      setBusy(true);
      try {
        await axios.delete(`${baseUrl}/approval-policies/${selectedPolicy.id}/steps/${stepId}`, { headers: tokenHeader() });
        notify("Step deleted");
        await selectPolicy(selectedPolicy.id);
      } catch (e) {
        handleErr(e, "Failed to delete step");
      } finally {
        setBusy(false);
      }
    },
    [handleErr, notify, selectPolicy, selectedPolicy]
  );

  const moveStep = useCallback(
    async (index: number, dir: -1 | 1) => {
      if (!selectedPolicy) return;
      const steps = [...(selectedPolicy.steps || [])].sort((a, b) => a.stepOrder - b.stepOrder);
      if (index + dir < 0 || index + dir >= steps.length) return;
      const a = steps[index];
      const b = steps[index + dir];
      setBusy(true);
      try {
        await axios.patch(`${baseUrl}/approval-policies/${selectedPolicy.id}/steps/${a.id}`, { stepOrder: b.stepOrder }, { headers: tokenHeader() });
        await axios.patch(`${baseUrl}/approval-policies/${selectedPolicy.id}/steps/${b.id}`, { stepOrder: a.stepOrder }, { headers: tokenHeader() });
        await selectPolicy(selectedPolicy.id);
      } catch (e) {
        handleErr(e, "Failed to reorder steps");
      } finally {
        setBusy(false);
      }
    },
    [handleErr, selectPolicy, selectedPolicy]
  );

  /** -------- Users scoped search (with cancel + debounce) -------- */
  type UserScope = "ALL" | "DEPARTMENT" | "PROJECT" | "POSITION";
  const [userScope, setUserScope] = useState<UserScope>("ALL");
  const [selectedDeptId, setSelectedDeptId] = useState<number | null>(null);
  const [selectedProjId, setSelectedProjId] = useState<number | null>(null);
  const [selectedPosId, setSelectedPosId] = useState<number | null>(null);
  const [userQ, setUserQ] = useState("");
  const [userOptions, setUserOptions] = useState<UserLite[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const debounceRef = useRef<number | undefined>(undefined);
  const [debouncedUserQ, setDebouncedUserQ] = useState("");

  useEffect(() => {
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => setDebouncedUserQ(userQ.trim()), 300);
    return () => window.clearTimeout(debounceRef.current);
  }, [userQ]);

  const fetchUsers = useCallback(async () => {
    userCtrlRef.current?.abort();
    const ctrl = new AbortController();
    userCtrlRef.current = ctrl;

    try {
      setLoadingUsers(true);
      let data: any[] = [];

      if (userScope === "ALL") {
        // Search all users from /users endpoint
        const res = await axios.get(`${baseUrl}/users`, {
          headers: tokenHeader(),
          params: { 
            take: 100,
            ...(debouncedUserQ && { name: debouncedUserQ })
          },
          signal: ctrl.signal as any,
        });
        // Backend returns { users: [], totalCount: number }
        data = res.data?.users || (Array.isArray(res.data) ? res.data : []);
      } else if (userScope === "DEPARTMENT") {
        if (!selectedDeptId) {
          setUserOptions([]);
          return;
        }
        const res = await axios.get(`${baseUrl}/users`, {
          headers: tokenHeader(),
          params: { departmentId: selectedDeptId, take: 100, ...(debouncedUserQ && { name: debouncedUserQ }) },
          signal: ctrl.signal as any,
        });
        data = res.data?.users || (Array.isArray(res.data) ? res.data : []);
      } else if (userScope === "PROJECT") {
        if (!selectedProjId) {
          setUserOptions([]);
          return;
        }
        const res = await axios.get(`${baseUrl}/users`, {
          headers: tokenHeader(),
          params: { projectId: selectedProjId, take: 100, ...(debouncedUserQ && { name: debouncedUserQ }) },
          signal: ctrl.signal as any,
        });
        data = res.data?.users || (Array.isArray(res.data) ? res.data : []);
      } else if (userScope === "POSITION") {
        if (!selectedPosId) {
          setUserOptions([]);
          return;
        }
        const res = await axios.get(`${baseUrl}/users`, {
          headers: tokenHeader(),
          params: { positionId: selectedPosId, take: 100, ...(debouncedUserQ && { name: debouncedUserQ }) },
          signal: ctrl.signal as any,
        });
        data = res.data?.users || (Array.isArray(res.data) ? res.data : []);
      }

      const mapped: UserLite[] = (data || [])
        .map((u: any) => ({
          id: u?.id ?? u?.userId ?? 0,
          username: u?.username ?? u?.user?.username,
          email: u?.profile?.email ?? u?.email ?? u?.user?.email,
          profile: u?.profile ?? u?.user?.profile ?? {},
          name: u?.profile?.name ?? u?.name,
        }))
        .filter((u: UserLite) => !!u.id);

      const uniq: Record<number, UserLite> = {};
      mapped.forEach((u) => {
        uniq[u.id] = u;
        usersCache.set(u.id, u);
      });
      setUserOptions(Object.values(uniq));
    } catch (e) {
      if ((e as any)?.name !== "CanceledError") {
        setUserOptions([]);
      }
    } finally {
      setLoadingUsers(false);
    }
  }, [debouncedUserQ, selectedDeptId, selectedPosId, selectedProjId, userScope]);

  useEffect(() => {
    if (!openStepDialog.open) return;
    fetchUsers();
  }, [fetchUsers, openStepDialog.open, selectedDeptId, selectedProjId, selectedPosId, userScope]);

  // Pre-load departments, projects, and positions when dialog opens
  useEffect(() => {
    if (openStepDialog.open) {
      loadDepts();
      loadProjs();
      loadPos();
    }
  }, [openStepDialog.open, loadDepts, loadProjs, loadPos]);

  /** -------- Ordered steps memo -------- */
  const orderedSteps = useMemo(
    () => (selectedPolicy?.steps ?? []).slice().sort((a, b) => a.stepOrder - b.stepOrder),
    [selectedPolicy, tick]
  );

  /** -------- UI -------- */

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Approval Policies</Typography>
        <Box display="flex" alignItems="center" gap={2}>
          {busy && <CircularProgress size={22} />}
          <Button variant="outlined" onClick={loadPolicies}>Refresh</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreatePolicy}>New Policy</Button>
        </Box>
      </Stack>

      <Grid container spacing={2}>
        {/* Left: policies list */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Policies</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1}>
                {policies.length === 0 && !busy && (
                  <Typography color="text.secondary">No policies yet or wrong API URL.</Typography>
                )}
                {busy && policies.length === 0 && (
                  <>
                    <Skeleton variant="rounded" height={64} />
                    <Skeleton variant="rounded" height={64} />
                  </>
                )}
                {policies.map((p) => (
                  <Card
                    key={p.id}
                    variant="outlined"
                    sx={{ p: 1, borderColor: selectedPolicyId === p.id ? "primary.main" : undefined }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Box
                        onClick={() => selectPolicy(p.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && selectPolicy(p.id)}
                        sx={{ cursor: "pointer" }}
                      >
                        <Typography fontWeight={600}>{p.name}</Typography>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip size="small" label={p.taskType} />
                          <Chip size="small" label={`v${p.version}`} />
                          <Chip size="small" color={p.isActive ? "success" : "default"} label={p.isActive ? "Active" : "Inactive"} />
                        </Stack>
                      </Box>
                      <Tooltip title="Edit policy">
                        <IconButton onClick={() => openEditPolicy(p)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right: steps of selected policy */}
        <Grid item xs={12} md={8}>
          {!selectedPolicy ? (
            <Card>
              <CardContent>
                <Typography>Select a policy to manage its steps.</Typography>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="h6">{selectedPolicy.name} — Steps</Typography>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={openAddStep}>Add Step</Button>
                </Stack>

                <Tabs value={viewTab} onChange={(_, v) => setViewTab(v)} sx={{ mb: 2 }}>
                  <Tab value="list" icon={<ListAltIcon />} iconPosition="start" label="Steps List" />
                  <Tab value="diagram" icon={<TimelineIcon />} iconPosition="start" label="Workflow Diagram" />
                </Tabs>

                {viewTab === "list" && (
                  <Stack spacing={1}>
                    {orderedSteps.length === 0 && <Typography color="text.secondary">No steps yet — add one.</Typography>}
                    {orderedSteps.map((s, idx) => (
                      <StepCard
                        key={s.id}
                        step={s}
                        idx={idx}
                        total={orderedSteps.length}
                        dicts={{ users: usersCache, positions: positionsCache, departments: departmentsCache, projects: projectsCache }}
                        onMove={moveStep}
                        onEdit={openEditStep}
                        onDelete={deleteStepAction}
                      />
                    ))}
                  </Stack>
                )}

                {viewTab === "diagram" && (
                  <Box sx={{ px: 1, py: 2 }}>
                    <Stepper orientation="vertical" activeStep={-1}>
                      {orderedSteps.map((s) => (
                        <Step key={s.id} expanded>
                          <StepLabel>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip size="small" label={`Step ${s.stepOrder}`} />
                              <Chip size="small" color="primary" label={s.actorType} />
                              <Chip size="small" color={s.requiredStrategy === "ALL" ? "secondary" : "success"} label={s.requiredStrategy} />
                            </Stack>
                          </StepLabel>
                          <StepContent TransitionProps={{ unmountOnExit: false }}>
                            {/* reuse StepCard details part by composing minimal JSX */}
                            <StepCard
                              step={s}
                              idx={0}
                              total={1}
                              dicts={{ users: usersCache, positions: positionsCache, departments: departmentsCache, projects: projectsCache }}
                              onMove={() => {}}
                              onEdit={() => {}}
                              onDelete={() => {}}
                            />
                          </StepContent>
                        </Step>
                      ))}
                    </Stepper>
                    {orderedSteps.length === 0 && <Typography color="text.secondary">No steps to display.</Typography>}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Create / Edit Policy Dialog */}
      <Dialog open={openPolicyDialog} onClose={() => setOpenPolicyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedPolicy ? "Edit Policy" : "New Policy"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Controller
              name="workspaceId"
              control={pc}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label="Workspace ID"
                  fullWidth
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value === "" ? "" : toInt(e.target.value))}
                  inputProps={{ step: 1 }}
                />
              )}
            />
            <Controller name="name" control={pc} rules={{ required: true }} render={({ field }) => <TextField {...field} label="Name" fullWidth />} />
            <Controller name="taskType" control={pc} rules={{ required: true }} render={({ field }) => <TextField {...field} label="Task Type (e.g., VACATION)" fullWidth />} />
            <Controller
              name="version"
              control={pc}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label="Version"
                  fullWidth
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value === "" ? "" : toInt(e.target.value))}
                  inputProps={{ step: 1, min: 1 }}
                />
              )}
            />
            <Controller
              name="isActive"
              control={pc}
              render={({ field }) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography>Active</Typography>
                  <Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                </Stack>
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPolicyDialog(false)}>Cancel</Button>
          {selectedPolicy ? (
            <Button variant="contained" onClick={onUpdatePolicy}>Save</Button>
          ) : (
            <Button variant="contained" onClick={onCreatePolicy}>Create</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Add/Edit Step Dialog */}
      <Dialog open={openStepDialog.open} onClose={() => setOpenStepDialog({ open: false })} maxWidth="md" fullWidth>
        <DialogTitle>{openStepDialog.stepId ? "Edit Step" : "Add Step"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Controller
              name="stepOrder"
              control={sc}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label="Step Order"
                  fullWidth
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value === "" ? "" : toInt(e.target.value))}
                  inputProps={{ step: 1, min: 1 }}
                />
              )}
            />
            <Controller
              name="actorType"
              control={sc}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField {...field} select label="Actor Type" fullWidth>
                  {["USER", "POSITION", "ROLE", "MANAGER_OF_REQUESTER", "DEPARTMENT_MANAGERS", "PROJECT_MANAGERS"].map((at) => (
                    <MenuItem key={at} value={at}>{at}</MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="requiredStrategy"
              control={sc}
              render={({ field }) => (
                <TextField {...field} select label="Required Strategy" fullWidth>
                  {["ALL", "ANY"].map((v) => (
                    <MenuItem key={v} value={v}>{v}</MenuItem>
                  ))}
                </TextField>
              )}
            />

            {/* USER actor */}
            {actorType === "USER" && (
             <UserChooser
 control={sc}
 loadDepts={loadDepts}
 loadProjs={loadProjs}
 loadPos={loadPos}
 deptOptions={deptOptions}
 projOptions={projOptions}
 posOptions={posOptions}
 loadingDepts={loadingDepts}
 loadingProjs={loadingProjs}
 loadingPos={loadingPos}
 userQ={userQ}
 setUserQ={setUserQ}
 setValue={setValue}
 userOptions={userOptions}
 loadingUsers={loadingUsers}
 setUserOptions={setUserOptions}
 setSelectedDeptId={setSelectedDeptId}
 setSelectedProjId={setSelectedProjId}
 setSelectedPosId={setSelectedPosId}
 selectedDeptId={selectedDeptId}
 selectedProjId={selectedProjId}
 selectedPosId={selectedPosId}
 setUserScope={setUserScope}
 userScope={userScope}
 fetchUsers={fetchUsers}
/>

            )}

            {/* POSITION actor */}
            {actorType === "POSITION" && (
              <Controller
                name="actorPositionId"
                control={sc}
                rules={{ required: true }}
                render={({ field }) => (
                  <Autocomplete
                    options={posOptions}
                    loading={loadingPos}
                    onOpen={loadPos}
                    getOptionLabel={(o) => posLabel(o)}
                    isOptionEqualToValue={(o, v) => o.id === (v as any)?.id}
                    value={posOptions.find((p) => p.id === toInt(field.value)) || null}
                    onChange={(_, v) => field.onChange(v ? (v as Pos).id : "")}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Position"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingPos ? <CircularProgress size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )}
              />
            )}

            {/* ROLE actor */}
            {actorType === "ROLE" && (
              <Controller
                name="actorRole"
                control={sc}
                rules={{ required: true }}
                render={({ field }) => <TextField {...field} label="Role (e.g., HR, GM)" fullWidth />}
              />
            )}

            {/* Shared toggles */}
            <Controller
              name="sameDepartmentAsRequester"
              control={sc}
              render={({ field }) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography>Same Department As Requester</Typography>
                  <Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                </Stack>
              )}
            />
            <Controller
              name="useTaskProject"
              control={sc}
              render={({ field }) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography>Use Task Project</Typography>
                  <Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                </Stack>
              )}
            />
            <Controller
              name="useProjectDepartment"
              control={sc}
              render={({ field }) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography>Use Project Department</Typography>
                  <Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                </Stack>
              )}
            />

            {/* targetDepartmentId */}
            <Controller
              name="targetDepartmentId"
              control={sc}
              render={({ field }) => (
                <Autocomplete
                  options={deptOptions}
                  loading={loadingDepts}
                  onOpen={loadDepts}
                  getOptionLabel={(o) => deptLabel(o)}
                  isOptionEqualToValue={(o, v) => o.id === (v as any)?.id}
                  value={deptOptions.find((d) => d.id === toInt(field.value)) || null}
                  onChange={(_, v) => field.onChange(v ? (v as Dept).id : "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Target Department (optional)"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingDepts ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
            {/* targetProjectId */}
            <Controller
              name="targetProjectId"
              control={sc}
              render={({ field }) => (
                <Autocomplete
                  options={projOptions}
                  loading={loadingProjs}
                  onOpen={loadProjs}
                  getOptionLabel={(o) => projLabel(o)}
                  isOptionEqualToValue={(o, v) => o.id === (v as any)?.id}
                  value={projOptions.find((p) => p.id === toInt(field.value)) || null}
                  onChange={(_, v) => field.onChange(v ? (v as Proj).id : "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Target Project (optional)"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingProjs ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
            <Controller
              name="autoAdvanceIfNone"
              control={sc}
              render={({ field }) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography>Auto-advance if no approver found</Typography>
                  <Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                </Stack>
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStepDialog({ open: false })}>Cancel</Button>
          <Button variant="contained" onClick={onSubmitStep}>{openStepDialog.stepId ? "Save" : "Add"}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} sx={{ width: "100%" }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}

/** ========== User chooser sub-component (extracted for clarity & perf) ========== */
type UserChooserProps = {
  loadDepts: () => Promise<void>;
  loadProjs: () => Promise<void>;
  loadPos: () => Promise<void>;
  deptOptions: Dept[];
  projOptions: Proj[];
  posOptions: Pos[];
  loadingDepts: boolean;
  loadingProjs: boolean;
  loadingPos: boolean;
  userQ: string;
  setUserQ: (v: string) => void;
  setValue: (name: any, v: any) => void;
  userOptions: UserLite[];
  loadingUsers: boolean;
  setUserOptions: (v: UserLite[]) => void;
  setSelectedDeptId: (v: number | null) => void;
  setSelectedProjId: (v: number | null) => void;
  setSelectedPosId: (v: number | null) => void;
  selectedDeptId: number | null;
  selectedProjId: number | null;
  selectedPosId: number | null;
  setUserScope: (s: "ALL" | "DEPARTMENT" | "PROJECT" | "POSITION") => void;
  userScope: "ALL" | "DEPARTMENT" | "PROJECT" | "POSITION";
  control: Control<StepForm>;
  fetchUsers: () => Promise<void>;
};
function UserChooser({
  loadDepts,
  loadProjs,
  loadPos,
  deptOptions,
  projOptions,
  posOptions,
  loadingDepts,
  loadingProjs,
  loadingPos,
  userQ,
  setUserQ,
  setValue,
  userOptions,
  loadingUsers,
  setUserOptions,
  setSelectedDeptId,
  setSelectedProjId,
  setSelectedPosId,
  selectedDeptId,
  selectedProjId,
  selectedPosId,
  setUserScope,
  userScope,
  control,
  fetchUsers,
}: UserChooserProps) {
  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">Select Approver (User)</Typography>

      <TextField
        select
        label="Find user by"
        size="small"
        value={userScope}
        onChange={async (e) => {
          const val = (e.target.value as "ALL" | "DEPARTMENT" | "PROJECT" | "POSITION") || "ALL";
          setUserScope(val);
          setUserQ("");
          setUserOptions([]);
          setValue("actorUserId", "");
          // If ALL is selected, immediately load all users
          if (val === "ALL") {
            await fetchUsers();
          }
        }}
        sx={{ maxWidth: 260 }}
      >
        <MenuItem value="ALL">All Users</MenuItem>
        <MenuItem value="DEPARTMENT">Department</MenuItem>
        <MenuItem value="PROJECT">Project</MenuItem>
        <MenuItem value="POSITION">Position</MenuItem>
      </TextField>

      {userScope === "DEPARTMENT" && (
        <Autocomplete
          options={deptOptions}
          loading={loadingDepts}
          onOpen={loadDepts}
          getOptionLabel={(o) => deptLabel(o)}
          value={deptOptions.find((d) => d.id === selectedDeptId) || null}
          onChange={async (_, v) => {
            setSelectedDeptId(v?.id ?? null);
            setUserOptions([]);
            setValue("actorUserId", "");
            // Immediately fetch users for the selected department
            if (v?.id) {
              await fetchUsers();
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Department"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingDepts ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}

      {userScope === "PROJECT" && (
        <Autocomplete
          options={projOptions}
          loading={loadingProjs}
          onOpen={loadProjs}
          getOptionLabel={(o) => projLabel(o)}
          value={projOptions.find((p) => p.id === selectedProjId) || null}
          onChange={async (_, v) => {
            setSelectedProjId(v?.id ?? null);
            setUserOptions([]);
            setValue("actorUserId", "");
            // Immediately fetch users for the selected project
            if (v?.id) {
              await fetchUsers();
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Project"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingProjs ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}

      {userScope === "POSITION" && (
        <Autocomplete
          options={posOptions}
          loading={loadingPos}
          onOpen={loadPos}
          getOptionLabel={(o) => posLabel(o)}
          isOptionEqualToValue={(o, v) => o.id === (v as any)?.id}
          value={posOptions.find((p) => p.id === selectedPosId) || null}
          onChange={async (_, v) => {
            setSelectedPosId(v?.id ?? null);
            setUserOptions([]);
            setValue("actorUserId", "");
            // Immediately fetch users for the selected position
            if (v?.id) {
              await fetchUsers();
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Position"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingPos ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}

      <TextField
        size="small"
        placeholder="Search user by name/email…"
        value={userQ}
        onChange={(e) => setUserQ(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        disabled={
          (userScope === "DEPARTMENT" && !selectedDeptId) ||
          (userScope === "PROJECT" && !selectedProjId) ||
          (userScope === "POSITION" && !selectedPosId)
        }
      />

     <Controller
  name="actorUserId"
  control={control}
  render={({ field }) => (
    <Autocomplete
      options={userOptions}
      loading={loadingUsers}
      getOptionLabel={(o) => userLabel(o)}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      value={userOptions.find((u) => u.id === toInt(field.value)) || null}
      onChange={(_, v) => field.onChange(v ? v.id : "")}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar 
              sx={{ width: 24, height: 24 }} 
              src={option.profile?.imageUrl}
              alt={userLabel(option)}
            >
              {avatarInitials(option)}
            </Avatar>
            <Typography variant="body2">{userLabel(option)}</Typography>
          </Stack>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select user"
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingUsers ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )}
/>

    </Stack>
  );
}
